import { FC } from 'react';

import { Grid, Typography, styled, Button, Divider } from '@mui/material';
import { SignIn, SignOut } from 'phosphor-react';
import { Connector } from 'wagmi';

import { useSession } from '~components/hooks/useSession';
import { useIsMobile } from '~components/hooks/utils/useIsMobile';
import { primaryColors, secondaryColors } from '~components/theme/styles/colors';
import WagmiConnectorConfig, { WagmiConnectorIds } from '~config/etherConnectorConfig';

import { StyledLink } from '../../dropdown/bridges';

import { AccountPanel } from './accountPanel';
import { AccountPanelOption } from './accountPanelOption';

const disclaimer =
  'https://docs.google.com/document/d/e/2PACX-1vRAewaErhuxxT6Hxss_3IWNVw3-PlByipiDIXYwNhfVF0EDEp0CGbBPSjSzN9JHbmafM5BtU0KOf0w0/pub';
const tos =
  'https://docs.google.com/document/d/e/2PACX-1vR35fS4nqisK3TMMmULwrZBqRu-ujqShbrZU_3KeqU51yGWe_lQtqCFAoIC1Lg5f67n4mDKYbsbZ093/pub';

export const AccountDialSelection = styled(Button)({
  minWidth: 0,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: primaryColors[900],
  },
  transition:
    'border-width 0s linear, border-color 0.1s ease-in-out, margin 0s linear, background-color 0.1s ease-in-out',
});

const SelectionItem: React.FC<{ connector: Connector<any, any>; afterConnect: () => void }> = ({
  connector,
  afterConnect,
}) => {
  const { connect } = useSession();

  let connectorId = connector.id;

  if (connectorId === 'injected') {
    if (connector.name === WagmiConnectorConfig.bitkeepInjected.name) {
      connectorId = WagmiConnectorIds.BITKEEP_ID;
    }
  }

  return (
    <AccountPanelOption
      onClick={() => {
        connect(connector);
        afterConnect();
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item>{WagmiConnectorConfig[connectorId as WagmiConnectorIds].icon(24, 24)}</Grid>
        <Grid item xs>
          <Typography
            variant={'label3'}
            sx={{ textTransform: 'uppercase', color: primaryColors[400], textAlign: 'start' }}
          >
            {connector.name}
          </Typography>
        </Grid>
      </Grid>
    </AccountPanelOption>
  );
};

export const AccountDialConnection: FC<{ onConnect: () => void; forceOpen?: boolean }> = ({
  onConnect,
  forceOpen,
}) => {
  const { connectors, isConnected, disconnect } = useSession();
  const isMobile = useIsMobile();

  return (
    <AccountPanel
      titleContent={isConnected ? 'Disconnect' : 'Connect'}
      titleIcon={isConnected ? <SignOut size={16} weight="bold" /> : <SignIn size={16} weight="bold" />}
      forceOpen={forceOpen}
      onClick={isConnected && isMobile ? disconnect : undefined}
    >
      <Grid>
        {connectors.map(connector => {
          return (
            <Grid item key={connector.name} xs={12}>
              <SelectionItem connector={connector} afterConnect={onConnect} />
            </Grid>
          );
        })}
        <Divider sx={{ my: 2 }} />
        <Grid item xs={12}>
          <Typography textAlign="start" variant={'body3'} color={primaryColors[600]}>
            By connecting a wallet, you agree to Vesta Finance&apos;s &nbsp;
            <StyledLink
              sx={{
                textDecoration: 'underline',
                color: primaryColors[100],
                '&:hover': {
                  color: secondaryColors[200],
                },
              }}
              href={tos}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </StyledLink>
            &nbsp;and acknowledge that you have read and fully understood Vesta Finance&apos;s&nbsp;
            <StyledLink
              sx={{
                textDecoration: 'underline',
                color: primaryColors[100],
                '&:hover': {
                  color: secondaryColors[200],
                },
              }}
              href={disclaimer}
              target="_blank"
              rel="noopener noreferrer"
            >
              Disclaimer
            </StyledLink>
            .
          </Typography>
        </Grid>
      </Grid>
    </AccountPanel>
  );
};
